import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomEvents, DUMMY_THREAD_ID, StorageHelper, STRATEGY_CREATION_METHOD } from '@app/_helpers';
import { ConfigurationService, EventsService } from '@app/_shared';
import { PortfolioDisplayResponse } from '@app/_shared/modules/portfolio-form/portfolio.interface';
import { Observable } from 'rxjs';
import { debounceTime, finalize, map, share, shareReplay, tap } from 'rxjs/operators';
import { AIAssistantType } from '../../../../../../utProto/webEnums_pb';
import { FEEDBACK_TYPE, OneAiConfig, THINKING_MESSAGES, ThinkingMessage } from '../chat.constants';
import { AIConversationResponse, AccountUsage, OneAiRemoteConfig, PortfolioVersionDetails, PortfolioVersionResponse, SaveYamlRequest, SpecificPortfolioVersionApiResponse, ThreadMeta, ValidateYamlRequest } from '../chat.interface';
import { AIConversation } from '../chat.models';
import { BriefPortfolioModel } from '../../market-place/providers/market-place/market-place.models';
import { MarketPlaceService } from '../../market-place/providers/market-place/market-place.service';
import { MyStrategyType } from '../../my-strategies/my-strategies.consts';

@Injectable({
  providedIn: 'root'
})
export class ChatService {

  oneAiConfig: OneAiRemoteConfig;
  thinkingStates: Record<string, ThinkingMessage>
  validateYamlRequest: Observable<any>;
  private saveYamlRequests: Map<string, Observable<PortfolioDisplayResponse>> = new Map();
  threadPortfolioMap: Map<string, BriefPortfolioModel | PortfolioDisplayResponse> = new Map();

  editorMsgId: string = '';
  editorPortfolioId?: string;
  editorVersion: number | null = null;
  editorDescription: string | null = null;
  editorIsPreview: boolean = false;

  portfolioActiveVersionMap: Map<string, number> = new Map();
  portfolioVersionDetailsMap: Map<string, PortfolioVersionDetails> = new Map();
  private specificPortfolioVersionRequests = new Map<string, Observable<PortfolioVersionDetails>>();
  private readonly RESYNC_DEBOUNCE_MS = 250;
  private readonly RESYNC_AFTER_HIDDEN_MS = 30_000;
  private tabVisibilityListenerAttached = false;
  private tabHiddenAt: number | null = null;
  private artifactResyncTimeoutId?: ReturnType<typeof setTimeout>;

  threadActiveModelMap: Map<string, number> = new Map();
  dummyThread: ThreadMeta | null = null;
  _fixedAiOpened: boolean;
  private _currentFixedAiThreadId: string = DUMMY_THREAD_ID;

  constructor(
    private http: HttpClient,
    private conf: ConfigurationService,
    private events: EventsService,
    private marketPlaceService: MarketPlaceService,
  ) {
    this.oneAiConfig = this.conf.remoteConfigCacheObj?.ONE_AI_CONFIG || OneAiConfig;
    this.thinkingStates = this.conf.remoteConfigCacheObj?.AI_THINKING_STATES || THINKING_MESSAGES;
    if(StorageHelper.isFixedAiOpened === undefined) {
      StorageHelper.isFixedAiOpened = true;
    }
    this._fixedAiOpened = StorageHelper.isFixedAiOpened;
    this.attachTabVisibilityResyncListener();
  }

  /** Bust in-flight version cache so tab-return resync always hits the server. */
  refreshSpecificPortfolioVersion(portfolioID: string, version: string): Observable<PortfolioVersionDetails> {
    const requestKey = `${portfolioID}_${String(version)}`;
    this.specificPortfolioVersionRequests.delete(requestKey);
    return this.getSpecificPortfolioVersion(portfolioID, version);
  }

  private attachTabVisibilityResyncListener(): void {
    if (this.tabVisibilityListenerAttached || typeof document === 'undefined') {
      return;
    }
    this.tabVisibilityListenerAttached = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.tabHiddenAt = Date.now();
        return;
      }
      if (this.tabHiddenAt === null) {
        return;
      }
      const hiddenDurationMs = Date.now() - this.tabHiddenAt;
      this.tabHiddenAt = null;
      if (hiddenDurationMs < this.RESYNC_AFTER_HIDDEN_MS) {
        return;
      }
      clearTimeout(this.artifactResyncTimeoutId);
      this.artifactResyncTimeoutId = setTimeout(() => {
        this.events.publish(CustomEvents.AiArtifactResync);
      }, this.RESYNC_DEBOUNCE_MS);
    });
  }

  get fixedAiOpened() {
    return this._fixedAiOpened;
  }

  set fixedAiOpened(value: boolean) {
    this._fixedAiOpened = value;
    StorageHelper.isFixedAiOpened = value;
  }

  get currentFixedAiThreadId(): string {
    return this._currentFixedAiThreadId;
  }

  set currentFixedAiThreadId(value: string) {
    this._currentFixedAiThreadId = value || DUMMY_THREAD_ID;
  }

  /** Updates sidebar "last activity" for a thread without waiting for server list refresh. */
  touchThreadActivity(threadId: string, updatedAtSec: number = Math.floor(Date.now() / 1000)): void {
    if (!threadId || threadId === DUMMY_THREAD_ID) {
      return;
    }
    this.events.publish(CustomEvents.THREAD_TIME_UPDATE, { threadId, updated_at: updatedAtSec });
  }

  touchDummyThreadActivity(updatedAtSec: number = Math.floor(Date.now() / 1000)): void {
    if (this.dummyThread) {
      this.dummyThread.updated_at = updatedAtSec;
    }
    this.events.publish(CustomEvents.THREAD_TIME_UPDATE, {
      threadId: DUMMY_THREAD_ID,
      updated_at: updatedAtSec
    });
  }

  /**
   * Retrieves the conversations from the server.
   * @returns An observable that emits an array of strings representing the conversation IDs.
   */
  getConversations(): Observable<ThreadMeta[]> {
    const url = this.conf.applicationUrls.getConversations;
    return this.http.get<{ threads: ThreadMeta[] }>(url).pipe(
      map(res => res.threads
        .sort((a, b) => b.updated_at - a.updated_at)
      )
    );
  }

  /**
   * Moves cached per-thread state when listMessages recovers a deleted thread under a new ID.
   *
   * Triggered when GET listMessages/{oldId} returns threadID !== oldId (portfolio Edit after
   * thread delete). Callers must also re-key threadContents via storeFetchedConversation.
   */
  migrateThreadIdReferences(oldThreadId: string, newThreadId: string): void {
    if (!oldThreadId || !newThreadId || oldThreadId === newThreadId) {
      return;
    }

    const portfolio = this.threadPortfolioMap.get(oldThreadId);
    if (portfolio) {
      this.threadPortfolioMap.delete(oldThreadId);
      this.threadPortfolioMap.set(newThreadId, portfolio);
    }

    if (this.threadActiveModelMap.has(oldThreadId)) {
      const model = this.threadActiveModelMap.get(oldThreadId);
      this.threadActiveModelMap.delete(oldThreadId);
      this.threadActiveModelMap.set(newThreadId, model);
    }

    if (this._currentFixedAiThreadId === oldThreadId) {
      this._currentFixedAiThreadId = newThreadId;
    }
  }

  /**
   * Retrieves a conversation by its thread ID.
   * @param threadId The ID of the conversation thread.
   * @returns An observable that emits the AIConversation object.
   */
  getConversationById(threadId: string): Observable<AIConversation> {
    const url = `${this.conf.applicationUrls.getConversationByID}${threadId}`;
    return this.http.get<AIConversationResponse>(url, {
      params: {
        getPortfolioAssociatedWithThread: true
      }
    })
      .pipe(map(res => {
        // Request URL may be the stale ID; response.threadID is the source of truth after recovery.
        const authoritativeId = res.threadID;
        if (authoritativeId !== threadId) {
          this.migrateThreadIdReferences(threadId, authoritativeId);
        }
        if (res.portfolio) {
          this.threadPortfolioMap.set(authoritativeId, res.portfolio);
        }
        return new AIConversation(res);
      })
      );
  }

  /**
   * Sends a message to the specified conversation thread.
   * @param id The ID of the thread to send the message to.
   * @param message The message content to be sent.
   * @param username The name of the user sending the message.
   * @param isValidated Indicates whether the message is from a trusted/validated source.
   * @param assistantType The type of AI assistant to which the message is directed (defaults to ONE_AI).
   * @returns An Observable of the HTTP response.
   */
  sendMessage(
    id: string,
    message: string,
    username: string,
    isValidated: boolean,
    assistantType: number = AIAssistantType.AIASSISTANTTYPE_ONE_AI
  ) {
    const url = this.conf.applicationUrls.sendMessage;
    const body = {
      threadID: id === DUMMY_THREAD_ID ? null : id,
      value: message,
      client_name: username,
      assistant_type: assistantType,
      trusted: isValidated
    };
    return this.http.put(url, body);
  }

  sendFixYamlMessage(
    threadId: string,
    ymlData: string,
    portfolioId: string,
    validationErrors: string[],
    username: string,
    assistantType: number = AIAssistantType.AIASSISTANTTYPE_ONE_AI
  ): Observable<any> {
    const url = this.conf.applicationUrls.sendMessage;
    const body = {
      threadID: threadId === DUMMY_THREAD_ID ? null : threadId,
      type: 'fix_yaml',
      value: 'Please fix validation errors in my portfolio YAML And Generate New YAML',
      ymlData,
      portfolioId,
      validationErrors,
      client_name: username,
      assistant_type: assistantType,
      trusted: true,
    };
    return this.http.put(url, body);
  }

  /**
   * Stops the incoming response of the thread ID.
   * @param id The ID of the thread.
   */
  stopResponse(id: string) {
    const url = `${this.conf.applicationUrls.stopResponse}${id}/`;
    const body = {};
    return this.http.post(url, body);
  }

  /**
   * Creates a new conversation with the specified AI assistant type.
   * @param assistantType - The type of AI assistant to use for the new conversation. Defaults to ONE_AI.
   * @returns An Observable emitting an object containing the new threadID.
   */
  createNewConversation(assistantType: number = AIAssistantType.AIASSISTANTTYPE_ONE_AI): Observable<{ threadID: string }> {
    const url = `${this.conf.applicationUrls.createNewConversation}?aiAssistantType=${assistantType}`;
    return this.http.put<{ threadID: string }>(url, {});
  }

  /**
   * Toggles the "starred" status for a specific conversation thread.
   * @param threadId - The ID of the thread to toggle its starred status.
   */
  toggleStarThread(threadId: string) {
    const url = `${this.conf.applicationUrls.toggleStarThread}${threadId}/`;
    return this.http.put(url, {});
  }

  /**
   * Validates the YAML data.
   * @param yaml - The YAML data to be validated.
   * @returns A Promise that resolves with the validation result.
   */
  validateYaml(request: ValidateYamlRequest) {
    const url = this.conf.applicationUrls.validateYml;

    if (this.validateYamlRequest) { return this.validateYamlRequest; }

    this.validateYamlRequest = this.http.put(url, request).pipe(
      debounceTime(2000),
      share(),
      finalize(() => this.validateYamlRequest = null)
    );

    return this.validateYamlRequest;
  }

  /**
   * Saves a YAML portfolio.
   * @param threadId - The ID of the thread.
   * @param ymlData - The YAML data to be saved.
   */
  saveYamlPortfolio(
    threadId: string,
    ymlData: string,
    msgId: string,
    portfolioId?: string,
    useEditorPortfolioFallback: boolean = true
  ): Observable<PortfolioDisplayResponse> {
    const url = this.conf.applicationUrls.saveYmlPortfolio;
    const request: SaveYamlRequest = {
      creationMethod: STRATEGY_CREATION_METHOD.YML_PORTFOLIO_CREATION,
      threadId,
      ymlData,
      msgId,
    }
    const id = portfolioId ?? (useEditorPortfolioFallback ? this.editorPortfolioId : undefined);
    if (id) { request['id'] = id; }
    if (this.editorVersion !== null) { request['version'] = this.editorVersion; }
    if (this.editorDescription) { request['description'] = this.editorDescription; }

    const requestKey = this.getSaveYamlRequestKey(threadId, msgId, id, this.editorVersion, this.editorDescription, ymlData);
    const cached = this.saveYamlRequests.get(requestKey);
    if (cached) { return cached; }

    const request$ = this.http.put<{ portfolio: PortfolioDisplayResponse }>(url, request).pipe(
      debounceTime(2000),
      share(),
      map(res => {
        if (res.portfolio) { this.threadPortfolioMap.set(threadId, res.portfolio) }
        const hasVersion = res?.portfolio?.yamlInformation?.version !== null && res?.portfolio?.yamlInformation?.version !== undefined;
        if (hasVersion && id) {
          this.portfolioActiveVersionMap.set(id, res?.portfolio?.yamlInformation?.version);
        }
        this.editorIsPreview = false;
        this.events.publish(CustomEvents.AI_PORTFOLIOS_UPDATED);
        this.events.publish(CustomEvents.UpdateAiArtifact, res.portfolio, ymlData, hasVersion);
        return res.portfolio
      }),
      finalize(() => {
        this.saveYamlRequests.delete(requestKey);
      })
    );

    this.saveYamlRequests.set(requestKey, request$);

    return request$;
  }

  private getSaveYamlRequestKey(
    threadId: string,
    msgId: string,
    portfolioId: string | undefined,
    version: number | null,
    description: string | null,
    ymlData: string
  ): string {
    return [
      threadId,
      msgId,
      portfolioId || '',
      version ?? '',
      description || '',
      ymlData
    ].join('|');
  }

  /**
   * Deletes a conversation thread.
   * @param threadId The ID of the thread to delete.
   * @returns A promise that resolves when the thread is successfully deleted.
   */
  deleteConversation(threadId: string) {
    const url = `${this.conf.applicationUrls.deleteConversation}${threadId}/`;
    return this.http.delete(url);
  }

  /**
   * Retrieves the AI credits usage.
   * @returns An observable that emits the number of AI credits used.
   */
  getAiCredits(): Observable<AccountUsage> {
    const url = this.conf.applicationUrls.aiCreditUsage;
    return this.http.get<AccountUsage[]>(url).pipe(map(res => res?.[0]));
  }

  /**
   * Sends feedback for a specific message within a conversation thread.
   * @param threadId - The ID of the conversation thread.
   * @param messageId - The ID of the message to send feedback for.
   * @param isPositive - Indicates whether the feedback is positive (true) or negative (false).
   * @param feedback - Optional additional feedback data to include in the request body.
   * @returns An observable representing the HTTP POST request.
   */
  sendFeedback(
    threadId: string,
    messageId: string,
    isPositive: boolean,
    feedback: any = {}
  ) {
    const url = `${this.conf.applicationUrls.sendFeedback}${threadId}/feedback/${messageId}/`;
    const body = {
      ...feedback,
      feedback_type: isPositive ? FEEDBACK_TYPE.GOOD : FEEDBACK_TYPE.BAD
    };
    return this.http.post(url, body);
  }

  aiYamlTimeToApiNumber(value: unknown): number {
    if (value == null || value === '') {
      return 0;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.floor(value));
    }
    const s = String(value).trim();
    if (!s) {
      return 0;
    }
    if (/^\d{5,6}$/.test(s)) {
      return parseInt(s, 10);
    }
    const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
    if (!match) {
      return 0;
    }
    const h = Math.min(23, parseInt(match[1], 10) || 0);
    const m = Math.min(59, parseInt(match[2], 10) || 0);
    const sec = match[3] != null ? Math.min(59, parseInt(match[3], 10) || 0) : 0;
    return h * 10000 + m * 100 + sec;
  }

   /**
   * Maps YAML executeOnDays to API executeDays. When Mon–Fri (1–5) are all present,
   * adds 0 (ALL) if it is not already included.
   */
  buildExecuteDaysFromYaml(executeOnDays?: string[] | null): number[] {
    const weekdays: Record<string, number> = { MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, ALL: 0, SAT: 6, SUN: 7 };
    const arr =
      executeOnDays
        ?.map((day) => weekdays[day])
        .filter((n): n is number => typeof n === 'number') ?? [];
    const set = new Set(arr);
    if ([1, 2, 3, 4, 5].every((d) => set.has(d)) && !set.has(0)) {
      set.add(0);
    }
    return [...set].sort((a, b) => a - b);
  }

  /**
   * Clears cached portfolio-version state after a LISTALGOS delete.
   * Stale entries in portfolioVersionDetailsMap still carry portfolioDetails for old
   * inactive versions; without this, updateArtifactStatus can revert Deleted → Inactive.
   */
  clearPortfolioVersionCache(portfolioId: string): void {
    if (!portfolioId) {
      return;
    }
    const prefix = `${portfolioId}_`;
    for (const key of [...this.portfolioVersionDetailsMap.keys()]) {
      if (key.startsWith(prefix)) {
        this.portfolioVersionDetailsMap.delete(key);
      }
    }
    for (const key of [...this.specificPortfolioVersionRequests.keys()]) {
      if (key.startsWith(prefix)) {
        this.specificPortfolioVersionRequests.delete(key);
      }
    }
    this.portfolioActiveVersionMap.delete(portfolioId);
    for (const [threadId, portfolio] of this.threadPortfolioMap.entries()) {
      const id = (portfolio as PortfolioDisplayResponse).id || (portfolio as BriefPortfolioModel).portfolioID;
      if (id && String(id) === String(portfolioId)) {
        this.threadPortfolioMap.delete(threadId);
      }
    }
  }

  getPortfolioVersions(portfolioId: string): Observable<PortfolioVersionResponse> {
    const url = `${this.conf.applicationUrls.portfolioVersionsUrl}${portfolioId}/versions/`;
    return this.http.get<PortfolioVersionResponse>(url, {
      params: { getPortfolioDetails: true }
    });
  }

  getSpecificPortfolioVersion(portfolioID: string, version: string): Observable<PortfolioVersionDetails> {
    const requestKey = `${portfolioID}_${String(version)}`;
    const cached = this.specificPortfolioVersionRequests.get(requestKey);
    if (cached) return cached;
    const request$ = this.http.get<SpecificPortfolioVersionApiResponse>(`${this.conf.applicationUrls.portfolioUrl}/${portfolioID}/versions/${version}`)
        .pipe(
            map((res) => {
              let portfolio: BriefPortfolioModel | null = null;
              if(res.data){
                const length = res.data.portfolio_statuses[res.data.portfolio._id]?.length;
                const status = res.data.portfolio_statuses[res.data.portfolio._id]?.[length - 1];
                const backtestLength = res.data.backtest_statuses[res.data.portfolio._id]?.length;
                const backtestStatus = res.data.backtest_statuses[res.data.portfolio._id]?.[backtestLength -1];
                const dynamicCardFields = res.data.extra_dynamic_card_fields?.[res.data.portfolio._id] || null;
                portfolio = new BriefPortfolioModel(res.data.portfolio, status, backtestStatus, null, dynamicCardFields);
                if(res.data.original_portfolio_details){
                    portfolio.updateOriginalPortfolioDetails([res.data.original_portfolio_details[portfolio.originalPortfolioId]]);
                }

                this.marketPlaceService.upsertPortfolioInTypeCache(portfolio, MyStrategyType.Created);
              }

                const result: PortfolioVersionDetails = {
                    activeYamlInformation: res.activeYamlInformation,
                    createdAt: res.createdAt,
                    description: res.description,
                    portfolioId: res.portfolioId,
                    portfolioName: res.portfolioName,
                    version: res.version,
                    ymlData: res.ymlData,
                }
                if(portfolio){
                    result.portfolioDetails = portfolio;
                }
                // Set the active version for the portfolio, but only if it's newer than what we already know.
                // A stale version-fetch response (in-flight when a newer version was created) must not
                // overwrite a more-recent activeVersion already stored by a completed fetch or save.
                if (result.portfolioId && result.activeYamlInformation?.version) {
                    const currentActiveVersion = this.portfolioActiveVersionMap.get(result.portfolioId);
                    if (currentActiveVersion === undefined || Number(result.activeYamlInformation.version) > Number(currentActiveVersion)) {
                        this.portfolioActiveVersionMap.set(result.portfolioId, result.activeYamlInformation.version);
                    }
                }
                // Set the version details for the portfolio
                if (result.portfolioId && result.version) { this.portfolioVersionDetailsMap.set(`${result.portfolioId}_${result.version}`, result) }
                //  Update Old Map as we get the latest version details
                if (portfolioID === result.portfolioId && version === result.activeYamlInformation?.version?.toString() && result.portfolioDetails) { 
                    this.threadPortfolioMap.set(result.portfolioDetails?.aiAssistantThreadID, result.portfolioDetails);
                }                    
                return result;
            }),
            finalize(() => this.specificPortfolioVersionRequests.delete(requestKey)),
            shareReplay(1),
        );

        this.specificPortfolioVersionRequests.set(requestKey, request$);
        return request$;
    }
}
